import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { PolicyAcceptanceGuard } from 'app/shared/privacy/privacy.guard';

// @formatter:off

export const appRoutes: Route[] = [
    // Redirect empty path to '/home'
    { path: '', pathMatch: 'full', redirectTo: 'home' },

    // Redirect signed-in user to the '/home'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'home' },

    // Pantalla de fallback para errores no recuperables (chunk-load loop, etc.)
    {
        path: 'error-fallback',
        loadComponent: () => import('app/error-fallback/error-fallback.component').then((m) => m.ErrorFallbackComponent),
        data: { layout: 'empty' },
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            { path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes') },
            { path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes') },
            { path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes') },
            { path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes') },
            { path: 'sign-in/mfa', loadChildren: () => import('app/modules/auth/mfa/mfa.routes') },
            { path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes') },
            { path: 'auto-login', loadChildren: () => import('app/modules/auth/auto-login/auto-login.routes') },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard, PolicyAcceptanceGuard],
        canActivateChild: [AuthGuard, PolicyAcceptanceGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes') },
            { path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes') },
            { path: 'change-password', loadChildren: () => import('app/modules/auth/change-password/change-password.routes') },
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [{ path: 'landing', loadChildren: () => import('app/modules/general/home/home.routes') }],
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard, PolicyAcceptanceGuard],
        canActivateChild: [AuthGuard, PolicyAcceptanceGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            { path: 'home', loadChildren: () => import('app/modules/admin/home/home.routes') },
            { path: 'security', loadChildren: () => import('app/modules/admin/security/security.routes') },
        ],
    },

    {
        path: '',
        canActivate: [AuthGuard, PolicyAcceptanceGuard],
        canActivateChild: [AuthGuard, PolicyAcceptanceGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'permissions',
                loadChildren: () => import('app/modules/admin/permissions/permissions.routing').then((m) => m.permissionRoutes),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PolicyAcceptanceGuard],
        canActivateChild: [AuthGuard, PolicyAcceptanceGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'demos',
                loadChildren: () => import('app/modules/admin/demos/demos.routing').then((m) => m.demosRoutes),
            },
            {
                path: 'payments',
                loadChildren: () => import('app/modules/admin/payments/payments.routing').then((m) => m.paymentsRoutes),
            },
        ],
    },

    {
        path: '',
        canActivate: [AuthGuard, PolicyAcceptanceGuard],
        canActivateChild: [AuthGuard, PolicyAcceptanceGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'tasks',
                loadChildren: () => import('app/modules/admin/tasks/tasks.routing').then((m) => m.tasksRoutes),
            },
        ],
    },

    {
        path: '',
        canActivate: [AuthGuard, PolicyAcceptanceGuard],
        canActivateChild: [AuthGuard, PolicyAcceptanceGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'tasks-templates',
                loadChildren: () => import('app/modules/admin/tasks_templates/tasks_templates.routing').then((m) => m.tasksTemplatesRoutes),
            },
        ],
    },

    {
        path: '',
        canActivate: [AuthGuard, PolicyAcceptanceGuard],
        canActivateChild: [AuthGuard, PolicyAcceptanceGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'clients',
                loadChildren: () => import('app/modules/admin/clients/clients.routing').then((m) => m.clientsRoutes),
            },
            {
                path: 'provider',
                loadChildren: () => import('app/modules/admin/provider/provider.routing').then((m) => m.providerRoutes),
            },
        ],
    },

    {
        path: '',
        canActivate: [AuthGuard, PolicyAcceptanceGuard],
        canActivateChild: [AuthGuard, PolicyAcceptanceGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'users',
                loadChildren: () => import('app/modules/admin/users/users.routing').then((m) => m.usersRoutes),
            },
            {
                path: 'registration-requests',
                loadChildren: () => import('app/modules/admin/registration-requests/registration-requests.routing').then((m) => m.registrationRequestsRoutes),
            },
        ],
    },

    {
        path: '',
        canActivate: [AuthGuard, PolicyAcceptanceGuard],
        canActivateChild: [AuthGuard, PolicyAcceptanceGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'company',
                loadChildren: () => import('app/modules/admin/company/company.routing').then((m) => m.companyRoutes),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PolicyAcceptanceGuard],
        canActivateChild: [AuthGuard, PolicyAcceptanceGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'reports',
                loadChildren: () => import('app/modules/admin/reports/reports.routes').then((m) => m.reportsRoutes),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PolicyAcceptanceGuard],
        canActivateChild: [AuthGuard, PolicyAcceptanceGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'gps',
                loadChildren: () => import('app/modules/admin/gps/gps.routing').then((m) => m.gpsRoutes),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PolicyAcceptanceGuard],
        canActivateChild: [AuthGuard, PolicyAcceptanceGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'client',
                resolve: {
                    data: () => import('app/modules/admin/clients/current-client.resolver').then((m) => m.currentClientResolver),
                },
                children: [
                    { path: '', pathMatch: 'full', redirectTo: 'settings' },
                    { path: 'home', loadChildren: () => import('app/modules/admin/home/home.routes') },
                    { path: 'tasks', loadChildren: () => import('app/modules/admin/tasks/tasks.routing').then((m) => m.tasksRoutes) },
                    {
                        path: 'settings',
                        loadComponent: () => import('app/modules/admin/clients/portal/settings/settings.component').then((m) => m.ClientPortalSettingsComponent),
                    },
                    {
                        path: 'business-info',
                        loadComponent: () => import('app/modules/admin/clients/portal/business-info/business-info.component').then((m) => m.ClientPortalBusinessInfoComponent),
                    },
                    {
                        path: 'contact',
                        loadComponent: () => import('app/modules/admin/clients/portal/contact/contact.component').then((m) => m.ClientPortalContactComponent),
                    },
                    {
                        path: 'center-costs',
                        loadComponent: () => import('app/modules/admin/clients/portal/center-costs/center-costs.component').then((m) => m.ClientPortalCenterCostsComponent),
                    },
                    {
                        path: 'payment-history',
                        loadComponent: () => import('app/modules/admin/clients/portal/payment-history/payment-history.component').then((m) => m.ClientPortalPaymentHistoryComponent),
                    },
                    {
                        path: 'files',
                        loadComponent: () => import('app/modules/admin/clients/portal/files/files.component').then((m) => m.ClientPortalFilesComponent),
                    },
                ],
            },
        ],
    },
];
